/**
 * The human's way onto the board. A kind, a title, optionally a body; for a
 * lookup kind, the one field that kind needs. Enter saves (the labels rule);
 * Escape clears.
 */
import { useState } from 'react';
import type { BoardroomItemKind, BoardroomLinkKind } from '../../types';
import { KIND_LABELS, KIND_ORDER, isLookup } from './kinds';
import { PERIOD_OPTIONS, type PricingPeriod } from './pricing';

export interface NewItem {
  kind: BoardroomItemKind;
  title: string;
  body?: string | null;
  payload?: Record<string, unknown>;
  parent_id?: number | null;
  link_kind?: BoardroomLinkKind;
}

/** The plan fields a human types for a PRICING item. Empty amount = "not published". */
export interface PricingFields {
  amount: string;
  currency: string;
  period: PricingPeriod;
  unit: string;
}

export const EMPTY_PRICING: PricingFields = { amount: '', currency: 'USD', period: 'month', unit: 'flat' };

export function buildNewItem(
  kind: BoardroomItemKind,
  title: string,
  body: string,
  parentId?: number | null,
  pricing?: PricingFields,
): NewItem {
  const t = title.trim();
  const base: NewItem = { kind, title: t, body: body.trim() || null, parent_id: parentId ?? null };
  if (kind === 'LOOKUP_SERP' || kind === 'LOOKUP_TRENDS' || kind === 'LOOKUP_NEWS' || kind === 'LOOKUP_SOCIAL') base.payload = { keyword: t };
  else if (kind === 'LOOKUP_PAGE') base.payload = { url: t };
  else if (kind === 'LOOKUP_DEMAND') base.payload = { seeds: t.split(',').map(s => s.trim()).filter(Boolean) };
  else if (kind === 'PRICING') {
    const f = pricing ?? EMPTY_PRICING;
    const amount = f.amount.trim() === '' ? null : Number(f.amount);
    base.payload = {
      amount,
      currency: f.currency.trim().toUpperCase() || 'USD',
      period: amount === null ? null : f.period,
      unit: f.unit.trim() || 'flat',
      plan: t,
    };
    // A plan hangs under its product; the backend refuses any other parent.
    base.link_kind = 'PRICES';
  }
  return base;
}

const PLACEHOLDER: Partial<Record<BoardroomItemKind, string>> = {
  LOOKUP_SERP: 'keyword to search',
  LOOKUP_PAGE: 'https://… page to read',
  LOOKUP_DEMAND: 'seed terms, comma separated',
  LOOKUP_TRENDS: 'keyword for Google Trends',
  LOOKUP_NEWS: 'search the news (last 7 days)',
  LOOKUP_SOCIAL: 'search Bluesky and Hacker News (last 7 days)',
};

export default function AddItemForm({
  onAdd,
  parentId,
  compact = false,
  fixedKind,
}: {
  onAdd: (item: NewItem) => Promise<void>;
  parentId?: number | null;
  compact?: boolean;
  /** Lock the kind (the drawer's "add a plan" under a product); hides the picker. */
  fixedKind?: BoardroomItemKind;
}) {
  const [kind, setKind] = useState<BoardroomItemKind>(fixedKind ?? 'NOTE');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pricing, setPricing] = useState<PricingFields>(EMPTY_PRICING);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A plan with no product to hang under would be refused by the backend; say
  // so before the request rather than after.
  const orphanPlan = kind === 'PRICING' && parentId == null;

  const submit = async () => {
    if (!title.trim() || busy || orphanPlan) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(buildNewItem(kind, title, body, parentId, pricing));
      setTitle('');
      setBody('');
      setPricing(EMPTY_PRICING);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add the item');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
      <div className="flex items-center gap-2">
        {!fixedKind && (
          <select
            aria-label="Item kind"
            value={kind}
            onChange={e => setKind(e.target.value as BoardroomItemKind)}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1.5 py-1 text-xs"
          >
            {KIND_ORDER.map(k => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        )}
        <input
          aria-label="Item title"
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') {
              setTitle('');
              setBody('');
            }
          }}
          placeholder={PLACEHOLDER[kind] ?? 'What is it?'}
          className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2.5 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!title.trim() || busy || orphanPlan}
          className="px-2.5 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {isLookup(kind) ? 'Queue' : 'Add'}
        </button>
      </div>
      {kind === 'PRICING' && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300" data-testid="pricing-fields">
          <input
            aria-label="Amount"
            value={pricing.amount}
            onChange={e => setPricing(f => ({ ...f, amount: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && submit()}
            placeholder="amount · blank = not published"
            inputMode="decimal"
            className="w-40 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1.5 py-0.5"
          />
          <input
            aria-label="Currency"
            value={pricing.currency}
            onChange={e => setPricing(f => ({ ...f, currency: e.target.value }))}
            className="w-12 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1.5 py-0.5 uppercase"
          />
          <select
            aria-label="Period"
            value={pricing.period}
            onChange={e => setPricing(f => ({ ...f, period: e.target.value as PricingPeriod }))}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1 py-0.5"
          >
            {PERIOD_OPTIONS.map(o => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            aria-label="Unit"
            value={pricing.unit}
            onChange={e => setPricing(f => ({ ...f, unit: e.target.value }))}
            placeholder="per seat · per location · flat"
            className="w-44 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1.5 py-0.5"
          />
          {orphanPlan && (
            <span className="text-amber-700 dark:text-amber-300">add pricing from the product's drawer</span>
          )}
        </div>
      )}
      {!compact && !isLookup(kind) && (
        <textarea
          aria-label="Item body"
          value={body}
          onChange={e => setBody(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
          }}
          rows={2}
          placeholder="Detail (optional) - ⌘↵ to add"
          className="mt-1.5 w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      )}
      {error && (
        <div className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
